'use client'

import { BellRing, CalendarX, Check } from 'lucide-react'
import { useId, useState, useTransition } from 'react'

import { RADIUS, SURFACE_QUIET } from '@/lib/surface'
import { MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'
import { requestRestockAlert } from '@/server/restock'

/**
 * „Powiadom mnie, kiedy poziom wróci do sprzedaży."
 *
 * ── WHAT IT REPLACED ─────────────────────────────────────────────────────
 * A sentence: „Poziomy oznaczone «brak wolnych terminów» są chwilowo
 * zapełnione […] Napisz do nas, a odezwiemy się, gdy tylko zwolni się
 * miejsce." It explained the state and then asked the reader to do the
 * remembering — write a mail, and trust that somebody at the other end keeps a
 * list. The owner's call was to make that the product's job: *„zmieńmy na coś
 * takiego, że jakby powiadom mnie, kiedy poziom wróci do sprzedaży, i wtedy
 * ktoś taki dostaje maila"*.
 *
 * So the sentence stayed (it is the only place the page explains the empty
 * card) and gained the control it was describing: one field, one button, and a
 * letter sent by `powiadomienia-o-powrocie` the moment the level has days
 * again.
 *
 * ── ONE FIELD, NO CONSENT CHECKBOX ───────────────────────────────────────
 * Deliberately. The address is used once, for the levels it was given for, and
 * the row closes when the letter goes out — there is no list to join, so there
 * is nothing to consent to beyond the sentence over the button, which says
 * exactly what will happen. A tick box here would be theatre, and it would make
 * a two-second action feel like a subscription.
 *
 * ── THE ANSWER NEVER TELLS YOU WHO ELSE IS WAITING ───────────────────────
 * „Damy znać" is the same reply for a new address and for one already on the
 * list (see `addRestockAlerts`). A form that answered differently would be a
 * way to ask whether a given person is waiting for something.
 */
export function RestockAlertForm({
  offeringSlug,
  planSlugs,
  sourcePath,
}: {
  /** The service these levels belong to — resolved server-side. */
  readonly offeringSlug: string
  /** Every level on this page with no free days. */
  readonly planSlugs: readonly string[]
  readonly sourcePath?: string
}) {
  const fieldId = useId()
  const [email, setEmail] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pending, start] = useTransition()

  if (done) {
    return (
      <p
        className={cn(
          MICRO,
          SURFACE_QUIET,
          'mt-4 flex items-start gap-2.5 px-4 py-3 text-white/60',
          RADIUS.box,
        )}
      >
        <Check size={14} className="mt-px shrink-0 text-white/45" aria-hidden="true" />
        <span>
          Damy znać na <strong className="font-semibold text-white/80">{email}</strong>, gdy tylko
          ten poziom wróci do sprzedaży.
        </span>
      </p>
    )
  }

  return (
    <form
      // `noValidate`: the browser's own bubble is not translated and lands in
      // the wrong corner of a dark page. The rule is the same one the server
      // applies, said once here for the eye and once there for the record.
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        start(async () => {
          const result = await requestRestockAlert({
            offeringSlug,
            planSlugs: [...planSlugs],
            email,
            ...(sourcePath ? { sourcePath } : {}),
          })
          if (result.ok) setDone(true)
          else setError(result.error)
        })
      }}
      className={cn(
        SURFACE_QUIET,
        RADIUS.box,
        'mt-4 flex flex-col gap-3 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-5 sm:gap-y-2',
      )}
    >
      <label htmlFor={fieldId} className={cn(MICRO, 'flex items-start gap-2.5 text-white/50')}>
        <CalendarX size={14} className="mt-px shrink-0 text-white/35" aria-hidden="true" />
        {/* Jak wracają do sprzedaży, to nasza sprawa — klientowi wystarczy, że
            wrócą. Zdanie o deklaracjach ekipy odsłaniało kuchnię i stawiało
            warunek tam, gdzie miała być obietnica. */}
        <span>
          <strong className="font-semibold text-white/75">
            Zostaw adres, a napiszemy, gdy te poziomy wrócą do sprzedaży.
          </strong>
        </span>
      </label>

      <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
        <input
          id={fieldId}
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="twoj@email.pl"
          aria-label="Adres e-mail"
          aria-invalid={error ? true : undefined}
          className={cn(
            RADIUS.pill,
            'w-full border border-white/15 bg-white/[0.06] px-4 py-2 text-[0.85rem] text-white transition-colors placeholder:text-white/35 hover:border-white/25 focus:border-white/45 focus:outline-none sm:w-56',
          )}
        />
        <button
          type="submit"
          disabled={pending}
          className={cn(
            RADIUS.pill,
            'inline-flex shrink-0 items-center justify-center gap-2 bg-white px-5 py-2 text-[0.85rem] font-semibold text-neutral-900 transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100',
          )}
        >
          <BellRing size={14} aria-hidden="true" />
          {pending ? 'Zapisuję…' : 'Powiadom mnie'}
        </button>
      </div>

      {/* `sm:basis-full` puts the refusal on its own line under the row rather
          than squeezing the field, and `flex-wrap` on the form is what lets it.
          Nothing here links to the contact page any more — „napisz do nas" is
          what this control replaces, and the header still has Kontakt for
          anybody who wants a person. */}
      {error ? (
        <p role="alert" className={cn(MICRO, 'text-destructive/80 sm:basis-full')}>
          {error}
        </p>
      ) : null}
    </form>
  )
}
